import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation } from 'react-router-dom';
import Menu from './components/Menu';
import HomePage from './components/HomePage';
import Dashboard from './components/Dashboard';
import TransactionPage from './components/TransactionPage';
import CreateTransaction from './components/CreateTransaction';
import ImportTransactions from './components/ImportTransactions';
import ManageAccounts from './components/ManageAccounts';
import ManageSubjects from './components/ManageSubjects';
import ManageCategories from './components/ManageCategories';
import ManageRecurrences from './components/ManageRecurrences';
import ManageImportMappings from './components/ManageImportMappings';
import InvestmentsPage from './components/InvestmentsPage';
import FamilyPage from './components/FamilyPage';
import AcceptInvite from './components/AcceptInvite';
import Login from './components/Login';
import SignUp from './components/Signup';
import ProtectedRoute from './components/ProtectedRoute';
import AccountPanel from './components/AccountPanel';
import OnboardingTour, { isTourCompleted } from './components/OnboardingTour/OnboardingTour';
import { auth, redirectResult } from './services/firebase';
import { api } from './services/api';
import Icon from '@mdi/react';
import { mdiAccountOutline } from '@mdi/js';
import LensEuroIcon from './components/LensEuroIcon';
import useIsMobile from './hooks/useIsMobile';

import './styles/App.css';
import './styles/layout.css';
import './styles/buttons.css';
import './styles/tables.css';

const MENU_PINNED_KEY = 'menuPinned';

const PAGE_TITLES = {
  '/': 'Home',
  '/dashboard': 'Home',
  '/transactions': 'Transactions',
  '/new-transaction': 'New Transaction',
  '/import-transactions': 'Import Statement',
  '/manage-accounts': 'Manage Accounts',
  '/manage-subjects': 'Manage Subjects',
  '/manage-categories': 'Manage Categories',
  '/manage-recurrences': 'Manage Recurring Transactions',
  '/manage-import-mappings': 'Manage Import Mappings',
  '/investments': 'Investments',
  '/family': 'Family',
  '/accept-invite': 'Accept Invitation',
};

const AppBarTitle = () => {
  const location = useLocation();
  const pageTitle = PAGE_TITLES[location.pathname];
  return (
      <span className="app-bar__title">
        <span className="app-bar__app-icon">
          <LensEuroIcon size={1.1} />
        </span>
      <span className="app-bar__app-name">Money Lens</span>{pageTitle ? <span className="app-bar__page-name"> — {pageTitle}</span> : ''}
    </span>
  );
};

function App() {
  const [user, setUser] = useState(null); // Track the user's authentication state
  const [loading, setLoading] = useState(true); // Track loading state
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showAccountPanel, setShowAccountPanel] = useState(false);
  const [pendingInvitations, setPendingInvitations] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('pendingFamilyInvitations') || '[]');
    } catch { return []; }
  });
  const isMobile = useIsMobile();
  const [isMenuPinned, setIsMenuPinned] = useState(() => {
    const stored = localStorage.getItem(MENU_PINNED_KEY);
    return stored === null ? true : stored === 'true';
  });
  const [allowedPages, setAllowedPages] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('allowedPages') || '[]');
    } catch { return []; }
  });
  const [tourActive, setTourActive] = useState(false);

  const syncWithBackend = async (firebaseUser) => {
    if (firebaseUser == null) return;

    try {
      const idToken = await firebaseUser.getIdToken();
      const payload = {
        external_id: firebaseUser.uid,
        email: firebaseUser.email,
        id_token: idToken,
      };

      // Always try signin first. If the backend has no User row for this
      // Firebase identity (i.e. this is a first-time Google sign-in that
      // went through the popup/redirect path instead of the Signup form),
      // fall back to signup so the account is actually created. The
      // inviteToken stashed by Signup.js is forwarded so the backend can
      // associate the new account with its family invitation.
      let response;
      try {
        response = await api.post('signin', payload);
      } catch (signinErr) {
        if (signinErr.response?.status !== 404) throw signinErr;
        const inviteToken = localStorage.getItem('inviteToken') || '';
        response = await api.post('signup', { ...payload, invitation_token: inviteToken });
      }

      localStorage.setItem('token', response.data.token);
      localStorage.setItem('userSettings', JSON.stringify(response.data.settings));
      const invites = response.data.pending_family_invitations || [];
      setPendingInvitations(invites);
      localStorage.setItem('pendingFamilyInvitations', JSON.stringify(invites));
      if (response.data.invitation_accepted) {
        localStorage.setItem('invitationAccepted', 'true');
      }

      // Consume the one-shot signup flags so they don't leak into a later
      // session.
      localStorage.removeItem('inviteToken');
      localStorage.removeItem('redirectedFrom');

      // Check family membership to gate navigation
      let hasFamily = false;
      try {
        const famRes = await api.get('/family/me');
        hasFamily = !!famRes.data.role;
        localStorage.setItem('hasFamily', String(hasFamily));
      } catch {
        localStorage.setItem('hasFamily', 'false');
      }

      // Fetch the pages the user may access (gates the menu)
      try {
        const pagesRes = await api.get('/family/pages');
        setAllowedPages(pagesRes.data.pages || []);
        localStorage.setItem('allowedPages', JSON.stringify(pagesRes.data.pages || []));
      } catch {
        localStorage.setItem('allowedPages', '[]');
        setAllowedPages([]);
      }

      setUser(firebaseUser);

      // First visit: offer the guided tour. Users who already completed (or
      // skipped) it have the flag in their settings.
      if (!isTourCompleted(response.data.settings)) {
        setTourActive(true);
      }

    } catch (err) {
      console.error("Login error:", err);
      localStorage.removeItem('token');
      setUser(null);
    } finally {
      // When pinned on desktop, the menu starts open; on mobile or unpinned
      // it starts closed.
      setIsMenuPinned((pinned) => {
        setIsMenuOpen(pinned && !isMobile);
        return pinned;
      });
    }
  };

  useEffect(() => {
    console.log("Setting up auth state listener");

    // First, try to process any pending redirects
    const processRedirect = async () => {
      try {
        console.log("Checking for redirect result...");
        const result = await redirectResult();
        if (result && result.user) {
          console.log("Redirect result processed successfully:", result.user.email);
          syncWithBackend(result.user);
        }
      } catch (err) {
        console.error("Error processing redirect:", err);
      }
    };

    // Process any redirect first
    processRedirect();

    // Then set up the auth state listener
    const unsubscribe = auth.onAuthStateChanged(async (firebaseUser) => {
      console.log('Auth state changed:', firebaseUser ? 'User signed in' : 'User signed out');

      if (firebaseUser) {
        syncWithBackend(firebaseUser);
      } else {
        localStorage.removeItem('token');
        localStorage.removeItem('userSettings');
        setUser(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => setIsMenuOpen(false);

  const startTour = () => setTourActive(true);

  const toggleMenuPin = () => {
    setIsMenuPinned((prevPinned) => {
      const nextPinned = !prevPinned;
      localStorage.setItem(MENU_PINNED_KEY, String(nextPinned));
      // Pinning opens the menu (in-flow rail); unpinning collapses it so the
      // content reclaims the full width immediately.
      setIsMenuOpen(nextPinned);
      return nextPinned;
    });
  };

  if (loading) {
    return <div>Loading...</div>; // Show a loading indicator while checking auth state
  }

  return (
    <Router>
      <div className="app-shell">
        {user && (
          <>
            <Menu
              isMenuOpen={isMenuOpen}
              toggleMenu={toggleMenu}
              isMenuPinned={isMenuPinned}
              toggleMenuPin={toggleMenuPin}
              closeMenu={closeMenu}
              onOpenAccountPanel={() => setShowAccountPanel(true)}
              allowedPages={allowedPages}
            />
            {!isMenuPinned && isMenuOpen && (
              <div
                className="menu-backdrop"
                onClick={closeMenu}
                aria-hidden="true"
              />
            )}
          </>
        )}

        <div className={`app-content ${user ? 'with-menu' : 'without-menu'} ${user && isMenuPinned && isMenuOpen ? 'menu-pinned' : ''}`}>
          {user && (
            <div className="app-bar">
              {!(isMenuPinned && isMenuOpen) && (
                <button
                  type='button'
                  className="app-bar__toggle"
                  onClick={toggleMenu}
                  aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
                >
                  {isMenuOpen ? '✕' : '☰'}
                </button>
              )}
              <AppBarTitle />
              <button
                type='button'
                className="app-bar__account"
                onClick={() => setShowAccountPanel(true)}
                aria-label="Open account settings"
                data-tour="open-account-panel"
              >
                <Icon path={mdiAccountOutline} size={1} />
              </button>
            </div>
          )}

          <div className={`page-region ${user ? 'with-topbar' : ''}`}>
            <Routes>
              <Route path="/" element={user ? <Dashboard /> : <HomePage setUser={setUser} />} />
              <Route path="/login" element={<Login setUser={setUser} />} />
              <Route path="/signup" element={<SignUp setUser={setUser} />} />
              <Route path="/dashboard" element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              } />
              <Route path="/transactions" element={
                <ProtectedRoute>
                  <TransactionPage />
                </ProtectedRoute>
              } />
              <Route path="/new-transaction" element={
                <ProtectedRoute>
                  <CreateTransaction />
                </ProtectedRoute>
              } />
              <Route path="/import-transactions" element={
                <ProtectedRoute>
                  <ImportTransactions />
                </ProtectedRoute>
              } />
              <Route path="/manage-accounts" element={
                <ProtectedRoute>
                  <ManageAccounts />
                </ProtectedRoute>
              } />
              <Route path="/manage-subjects" element={
                <ProtectedRoute>
                  <ManageSubjects />
                </ProtectedRoute>
              } />
              <Route path="/manage-categories" element={
                <ProtectedRoute>
                  <ManageCategories />
                </ProtectedRoute>
              } />
              <Route path="/manage-recurrences" element={
                <ProtectedRoute>
                  <ManageRecurrences />
                </ProtectedRoute>
              } />
              <Route path="/manage-import-mappings" element={
                <ProtectedRoute>
                  <ManageImportMappings />
                </ProtectedRoute>
              } />
              <Route path="/investments" element={
                <ProtectedRoute>
                  <InvestmentsPage />
                </ProtectedRoute>
              } />
              <Route path="/family" element={
                <ProtectedRoute>
                  <FamilyPage />
                </ProtectedRoute>
              } />
              <Route path="/accept-invite" element={<AcceptInvite user={user} setUser={setUser} />} />
            </Routes>
          </div>
        </div>
      </div>
      {user && showAccountPanel && (
        <AccountPanel
          user={user}
          onClose={() => setShowAccountPanel(false)}
          onReplayTour={() => {
            setShowAccountPanel(false);
            startTour();
          }}
        />
      )}
      {user && (
        <OnboardingTour
          active={tourActive}
          menuOpen={isMenuOpen}
          openMenu={() => setIsMenuOpen(true)}
          onFinish={() => setTourActive(false)}
        />
      )}
    </Router>
  );
}

export default App;
